import { AddRounded, RemoveRounded } from '@mui/icons-material'
import { Box, Grid, IconButton, InputBase, Typography } from '@mui/material'
import { memo, useCallback, useMemo } from 'react'
import AnalyticsManager from '../../analytics/AnalyticsManager'

export type Props = {
    ivMultiplier: number
    handleIvMultiplierChange: (newIvMultiplier: number) => void
}

const IvMultiplierButton = ({ ivMultiplier, handleIvMultiplierChange: setIvMultiplier }: Props) => {
    const ivPlusBtnColor = useMemo(() => (ivMultiplier < 2 ? 'inherit' : undefined), [ivMultiplier])

    const ivMinusBtnColor = useMemo(() => (ivMultiplier > 0 ? 'inherit' : undefined), [ivMultiplier])

    const ivMinusDisabled = useMemo(() => ivMultiplier === 0, [ivMultiplier])
    const ivPlusDisabled = useMemo(() => ivMultiplier === 2, [ivMultiplier])

    const handleIvPlusClick = useCallback(() => {
        AnalyticsManager.logEvent("IV Changed", { source: 'strategy-builder-submenu', type: 'increment' })
        if (ivMultiplier < 2) {
            setIvMultiplier(Math.min(2, ivMultiplier + 0.05))
        }
    }, [ivMultiplier, setIvMultiplier])

    const handleIvMinusClick = useCallback(() => {
        AnalyticsManager.logEvent("IV Changed", { source: 'strategy-builder-submenu', type: 'decrement' })
        if (ivMultiplier > 0) {
            setIvMultiplier(Math.max(0, ivMultiplier - 0.05))
        }
    }, [ivMultiplier, setIvMultiplier])

    const ivMultiplierValue = useMemo(() => `${ivMultiplier.toFixed(2)}x`, [ivMultiplier])

    const ivLabel = useMemo(() => (<Typography fontSize={10} color={'text.secondary'}>IV MULTIPLIER</Typography>), [])
    const minusIcon = useMemo(() => (<RemoveRounded className="trade-panel-edit-icons" />), [])

    return (
        <Grid item gap={0} id={'iv-multiplier-input'} sx={{ display: 'flex', alignItems: 'flex-start', flexDirection: 'column' }}>
            <Grid item xs={12} textAlign={'left'}>
                <Typography variant="caption" component="span" color={'text.secondary'} className="trade-panel-text">
                    {ivLabel}
                </Typography>
            </Grid>
            <Grid item xs={12} textAlign={'center'}>
                <Typography className="trade-panel-boldest-text" variant="body2" component="span" color="primaryText" fontSize={'small'} sx={{ fontSize: '10px' }}>
                    <Box
                        component="span"
                        sx={{
                            height: '24px',
                            padding: 0,
                            display: 'flex',
                            flexDirection: 'row',
                            alignItems: 'center',
                            border: '1px solid',
                            borderColor: 'divider',
                            borderRadius: 1,
                            '& .MuiInputBase-input': { padding: '4px 0 5px' }
                        }}
                    >
                        <IconButton
                            className="order-buttons"
                            sx={{ width: '24px', height: '24px', padding: '5px', cursor: 'pointer' }}
                            color={ivMinusBtnColor}
                            disabled={ivMinusDisabled}
                            onClick={handleIvMinusClick}
                        >
                            {minusIcon}
                        </IconButton>
                        <InputBase
                            value={ivMultiplierValue}
                            className="order-custom-input"
                            sx={{
                                height: '100%',
                                textAlign: 'center',
                                backgroundColor: 'none',
                                width: '50px',
                                fontSize: '14px'
                            }}
                        />
                        <IconButton
                            className="order-buttons"
                            sx={{ width: '24px', height: '24px', padding: '5px', cursor: 'pointer' }}
                            color={ivPlusBtnColor}
                            disabled={ivPlusDisabled}
                            onClick={handleIvPlusClick}
                        >
                            <AddRounded className="trade-panel-edit-icons" />
                        </IconButton>
                    </Box>
                </Typography>
            </Grid>
        </Grid>
    )
}

export default memo(IvMultiplierButton)
